import { useState } from "react";
import { ApiException } from "../../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import { useConfirm } from "../../../../../hooks/useConfirm";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";
import { IUserRequest } from "../../../../../@types/user.type";
import { addOrUpdateUser, deleteUser, inviteNewMemberToGroup } from "../../../../../services/api/user/UserService";
import { IGroupRequest } from "../../../../../@types/group.type";
import { GroupList } from "../GroupList";
import { IUserInformation } from "../../../../../@types/user-information.type";
import { IInviteNewUser } from "../../../../../@types/invite-new-user.type";

const schema = z.object({
  id: z.string().optional(),
  firstname: z
    .string()
    .min(1, "O Primeiro Nome é obrigatório")
    .max(60, "O Primeiro Nome deve ter no máximo 60 caracteres"),
  lastname: z.string().max(100, "O Sobrenome deve ter no máximo 100 caracteres").optional(),
  email: z.string().optional(),
});

interface FormUsersProps {
  pageType: "create" | "update";
  userData?: IUserInformation;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export const FormUsers: React.FC<FormUsersProps> = ({ pageType, userData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IUserInformation>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: userData?.id,
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      email: userData?.email || "",
    },
  });

  const [groups, setGroups] = useState<IGroupRequest[]>(() => {
    const uniqueGroups = userData?.features
      ?.reduce((acc, feature) => {
        if (!acc.some((group) => group.groupId === feature.groupId)) {
          acc.push({
            groupId: feature.groupId,
            groupName: feature.groupName,
          });
        }
        return acc;
      }, [] as IGroupRequest[]) || [];

    return uniqueGroups;
  });
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o usuário?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();

  function erroMessage(message: string){
    switch (message) {
    case "EMAIL_ALREADY_USED_USER":
      return 'Usuário já existente.'
    default:
      return `Não foi possível enviar email! Tente novamente mais tarde.`
    }
  }

  const onSubmit = async (data: IUserRequest) => {
    try {
      setLoading(true);
      const userToApi = {
        id: userData?.id,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data?.email,
        groups: groups
      };
      const response = await addOrUpdateUser(userToApi);

      if (response.statusCode === 200) {
        if ((pageType === "create") || ((pageType === "update") && (userData?.lastAccess === null)))
        {
          const dataEmail : IInviteNewUser = {
            email: data.email || "",
            firstname: data.firstname || "",
            lastname: data?.lastname || ""
          };

          const responseEmail = await inviteNewMemberToGroup(dataEmail)

          if (responseEmail.statusCode === 200){
            toast.success("Email enviado com sucesso!");
          }
          else{
            toast.error(erroMessage(responseEmail.erroMessage));
          }
        }

        reset();
        toast.success("Usuário salvo com sucesso!");
        navigate("/configurations/users");
      }
      else
      {
        toast.error(erroMessage(response));
      }

    } catch (error: any) {
      toast.error("Erro ao salvar o Usuário");
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      const response = await deleteUser(userData?.id as string);
      if (response.statusCode === 200){
        toast.success("Usuário excluído com sucesso!");
        navigate("/configurations/users");
      }
      else{
        toast.error(
          `Não foi possível excluir o usuário! Tente novamente mais tarde.`
        );
      }
    }

  };

  const [value, setValue] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/users")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.titleTop}>
        <h1>{pageType === "update" ? `${userData?.firstname} ${userData?.lastname}` : "Novo Usuário"}</h1>
      </div>
      <div>
        <div className={styles.userTab}>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" classes={{
                    indicator: styles['MuiTabs-indicator'],
                }}>
                <Tab label="Dados do Usuário" {...a11yProps(0)} className={styles.tab}/>
            </Tabs>
        </Box>
        </div>
        <CustomTabPanel value={value} index={0}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.formUser}>
            <h3 className={styles.title}>Informações Gerais</h3>
            <div className={styles.fields}>
              <Controller
                name="firstname"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Primeiro Nome"
                    placeholder={
                      pageType === "create" ? "Insira o primeiro nome do usuário aqui..." : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.firstname}
                    helperText={errors.firstname?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="lastname"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Sobrenome"
                    placeholder={
                      pageType === "create" ? "Insira o sobrenome do usuário aqui..." : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.lastname}
                    helperText={errors.lastname?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Email"
                    placeholder={
                      pageType === "create" ? "Insira o email do usuário aqui..." : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                    InputProps={{ readOnly: pageType === "update" }} 
                    disabled={pageType === "update"}
                  />
                )}
              />
            </div>
            <div>
              <GroupList
                groups={groups}
                setGroups={setGroups}
              />
            </div>
            <div className={styles.btnContainer}>
              {userData?.id && (
                <Button
                  variant="contained"
                  color="secondary"
                  type="button"
                  onClick={handleDeleteClick}
                  className={styles.btnsDeleted}
                  disabled={loading}
                >
                  Excluir Usuário
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={styles.btnsSave}
                disabled={loading}
              >
              Salvar
              </Button>
            </div>
          </form>
        </CustomTabPanel>
      </div>
      {Confirm}
    </>
  );
};
